// The Talent read path's DECISIONS, as pure functions.
//
// WHY THIS FILE EXISTS. `useTalentResult` is a small hook wrapped around a
// genuinely awkward state machine: a request whose answer may already have
// arrived, may arrive later on a socket, may arrive for a selection the reader has
// since left, or may never arrive at all. Review found SIX separate ways it could
// strand a reader on a spinner, and every one was a decision — which event applies,
// when a cursor is stale, what status to report — rather than anything to do with
// React. None of them was reachable by any test in the repo, because testing them
// through the hook needs a renderer this app does not depend on.
//
// So the decisions live here, where a plain unit test can enumerate them, and the
// hook is left as the plumbing that carries them out. If you change how an answer
// is chosen or how a status is derived, change it HERE and add the case to
// `talent-result-state.test.ts`.
//
// The standing hazard, stated once: there is NO TIMER anywhere in this path, by
// design (doctrine 1.2). That is what makes it not-polling, and it is also why
// every mistake here presents identically — as a page that waits forever. A
// decision that drops an answer is not a slow page, it is a stuck one.

/** The one event this path listens for. Mirrors `TalentResultReady`
 *  (`recruiter/messaging/events.py`); snake_case on the wire. */
export interface TalentResultReadyEvent {
  result_key: string;
  ok: boolean;
  detail: string;
}

/** How far we have already acted, for the key we currently hold. */
export interface AppliedCursor {
  key: string;
  index: number;
}

export const NO_CURSOR: AppliedCursor = { key: "", index: -1 };

/** What to do with the event buffer, given the key we are waiting on. */
export type EventDecision =
  | { act: false; cursor: AppliedCursor }
  | { act: true; cursor: AppliedCursor; event: TalentResultReadyEvent };

/** Choose the event to act on, if any.
 *
 *  Rules, each of which is a bug that shipped to review:
 *
 *  1. **Never consume an event that is not ours.** An answer can be published while
 *     `activeKey` is still empty — the window between a selection changing and its
 *     POST reply landing, during which a job enqueued by an earlier tab or render
 *     can finish. A cursor advanced past it drops it for good.
 *  2. **Re-scan when the key changes**, so an event that arrived early is found once
 *     the key it answers turns up.
 *  3. **A cursor from a previous buffer means nothing.** `usePusherChannel` empties
 *     `events` on every re-subscribe; an index left over from the old array skips
 *     that many events on the new one.
 *  4. **Take the NEWEST match**, not the first: a re-answered selection (a rebuild
 *     landing behind a cached answer) should win over the answer it replaces.
 */
export function pickEvent(
  events: readonly (TalentResultReadyEvent | undefined)[],
  activeKey: string,
  applied: AppliedCursor,
): EventDecision {
  if (!activeKey) return { act: false, cursor: applied };

  // Rules 2 and 3: a new key, or a buffer that shrank under us, invalidates the index.
  const cursor: AppliedCursor =
    applied.key !== activeKey || events.length <= applied.index
      ? { key: activeKey, index: -1 }
      : applied;

  // Rule 4.
  let latest = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i]?.result_key === activeKey) {
      latest = i;
      break;
    }
  }
  // Rule 1: nothing matched, so nothing is consumed — the buffer keeps everything
  // for whichever key shows up next.
  if (latest <= cursor.index) return { act: false, cursor };

  const event = events[latest];
  if (!event) return { act: false, cursor };
  return { act: true, cursor: { key: activeKey, index: latest }, event };
}

/** What the page should render. */
export interface TalentResultView<TResult> {
  result: TResult | null;
  /** The warehouse is answering and there is nothing to show yet. */
  isPending: boolean;
  /** Something went wrong and no answer is coming. NEVER null while pending. */
  error: string | null;
  /** An answer was served and a fresher one was asked for. Informational only. */
  refreshing: boolean;
}

export interface ResponseLike<TResult> {
  status: "ready" | "pending" | "unavailable";
  resultKey: string;
  result?: TResult | null;
  detail?: string;
  refreshing?: boolean;
}

/** Fold the request's answer and any late-arriving one into what to render.
 *
 *  The rule that matters: **`isPending` is a promise that an answer is coming**, so
 *  it may only be true when something is actually going to deliver one. A
 *  `pending` response whose event already arrived and failed is an ERROR, not a
 *  wait — the server answers `unavailable` when it could not queue the work, and a
 *  failed event says so directly. Reporting either as pending is the spinner with
 *  no end that this whole file exists to prevent.
 *
 *  A late answer applies only to the key it answers. When the reader moves on,
 *  `activeKey` changes and the stale one stops applying by itself — no cleanup, no
 *  cancellation, no race to lose.
 */
export function resultView<TResult>(
  response: ResponseLike<TResult> | undefined,
  late: { key: string; result: TResult | null; error: string | null },
  activeKey: string,
): TalentResultView<TResult> {
  const lateApplies = late.key !== "" && late.key === activeKey;
  const result = (lateApplies ? late.result : null) ?? response?.result ?? null;
  const lateError = lateApplies && !late.result ? late.error : null;
  const responseError =
    result === null && response?.status === "unavailable" ? (response.detail ?? null) : null;
  const error = lateError ?? responseError;

  return {
    result,
    isPending: result === null && error === null && response?.status === "pending",
    error,
    refreshing: response?.refreshing ?? false,
  };
}
